<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <div class="mb-4">
            <a class="text-primary text-body-2" style="cursor: pointer;" @click="backToList">‹ {{ $t('labelOrder.label.backToList') }}</a>
          </div>
          <v-alert v-if="message" type="info" density="compact" closable class="mb-4" :text="message" @click:close="message = ''" />
          <v-alert v-if="errorMessage" type="error" density="compact" closable class="mb-4" :text="errorMessage" @click:close="errorMessage = ''" />

          <!-- 申請情報 -->
          <LabelMetaStrip :items="infoItems" class="mb-6" />

          <!-- 一括反映（表の操作） -->
          <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px;">
            <v-text-field
              v-model.number="bulkQty"
              type="number"
              min="0"
              step="500"
              :label="$t('labelOrder.label.bulkQty')"
              :hint="$t('labelOrder.label.bulkHint')"
              variant="underlined"
              density="compact"
              style="max-width: 240px;"
              class="text-right-input mr-2"
            />
            <v-btn variant="tonal" color="indigo" @click="bulkApply">{{ $t('labelOrder.action.bulkSelected') }}</v-btn>
            <v-btn variant="tonal" color="indigo" @click="bulkApplyAll">{{ $t('labelOrder.action.bulkAll') }}</v-btn>
            <v-btn variant="text" @click="clearQty">{{ $t('labelOrder.action.clearQty') }}</v-btn>
            <v-spacer></v-spacer>
            <span class="text-body-2 text-medium-emphasis">
              {{ $t('labelOrder.label.summary.notEntered') }} {{ notEnteredCount }} {{ $t('labelOrder.label.unitStore') }}
              <span class="mx-2">/</span>
              <span :class="lowStockCount > 0 ? 'text-amber-darken-3' : ''">
                {{ $t('labelOrder.label.summary.lowStock') }} {{ lowStockCount }} {{ $t('labelOrder.label.unitStore') }}
              </span>
            </span>
          </div>

          <!-- 店舗ごとの発注枚数 -->
          <v-table density="compact" class="text-no-wrap">
            <thead>
              <tr>
                <th style="width: 48px;">
                  <v-checkbox-btn :model-value="allSelected" :indeterminate="someSelected && !allSelected" @update:model-value="toggleAll" />
                </th>
                <th>{{ $t('labelOrder.label.lineTable.siteCode') }}</th>
                <th>{{ $t('labelOrder.label.lineTable.storeName') }}</th>
                <th>{{ $t('labelOrder.label.lineTable.janCode') }}</th>
                <th class="text-right">{{ $t('labelOrder.label.lineTable.remaining') }}</th>
                <th>{{ $t('labelOrder.label.lineTable.state') }}</th>
                <th class="text-right" style="width: 150px;">{{ $t('labelOrder.label.lineTable.qty') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in stores" :key="s.siteCode" :class="s.selected ? 'bg-blue-lighten-5' : ''">
                <td><v-checkbox-btn v-model="s.selected" /></td>
                <td class="text-medium-emphasis">{{ s.siteCode }}</td>
                <td class="font-weight-medium">{{ s.storeName }}</td>
                <td class="text-medium-emphasis">{{ s.janCode }}</td>
                <td class="text-right font-weight-bold" :class="s.remainingQty < 0 ? 'text-red-darken-1' : ''">{{ num(s.remainingQty) }}</td>
                <td>
                  <v-chip size="x-small" label variant="tonal" :color="remainColor(s.remainingQty)">
                    {{ $t('labelStock.label.state.' + remainState(s.remainingQty)) }}
                  </v-chip>
                </td>
                <td class="text-right">
                  <!-- 選択していない行は申請に含まれないので、入力値を薄く表示する -->
                  <v-text-field
                    :model-value="s.qty"
                    @update:model-value="setQty(s, $event)"
                    type="number"
                    min="0"
                    step="500"
                    placeholder="0"
                    variant="underlined"
                    density="compact"
                    hide-details
                    class="text-right-input"
                    :class="s.selected ? '' : 'qty-unselected'"
                  />
                </td>
              </tr>
              <tr v-if="!isLoading && stores.length === 0">
                <td colspan="7" class="text-center text-medium-emphasis">
                  {{ $t('common.label.all') }} 0 {{ $t('common.label.totalCountOf') }}
                </td>
              </tr>
            </tbody>
          </v-table>
          <v-progress-linear v-if="isLoading" indeterminate color="indigo" />

          <!-- 申請（画面下に固定。店舗が多くてもボタンが見える） -->
          <div class="label-footer d-flex align-center">
            <span class="text-body-2 text-medium-emphasis">{{ $t('labelOrder.label.selectedStores') }}</span>
            <span class="text-h6 mx-2">{{ selectedLines.length }}</span>
            <span class="text-body-2 text-medium-emphasis mr-6">{{ $t('labelOrder.label.unitStore') }}</span>
            <span class="text-body-2 text-medium-emphasis">{{ $t('labelOrder.label.total') }}</span>
            <span class="text-h6 text-blue-darken-2 mx-2">{{ num(selectedTotal) }}</span>
            <span class="text-body-2 text-medium-emphasis">{{ $t('labelOrder.label.unitSheet') }}</span>
            <v-spacer></v-spacer>
            <v-btn variant="text" class="mr-2" @click="backToList">{{ $t('labelOrder.action.back') }}</v-btn>
            <v-btn class="px-8 bg-indigo-darken-1" elevation="0" :disabled="selectedLines.length === 0" @click="openConfirm">
              {{ $t('labelOrder.action.submit') }}
            </v-btn>
          </div>
        </v-container>

        <!-- 確認モーダル -->
        <v-dialog v-model="confirmDialog" width="640" persistent scrollable>
          <v-card>
            <v-card-title>{{ $t('labelOrder.dialog.confirmTitle') }}</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="text-body-2 mb-4">{{ $t('labelOrder.dialog.confirmText') }}</div>
              <v-alert v-if="submitError" type="error" density="compact" class="mb-4" :text="submitError" />
              <v-table density="compact" height="300" fixed-header>
                <thead>
                  <tr>
                    <th>{{ $t('labelOrder.label.lineTable.siteCode') }}</th>
                    <th>{{ $t('labelOrder.label.lineTable.storeName') }}</th>
                    <th class="text-right">{{ $t('labelOrder.label.lineTable.qty') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in selectedLines" :key="s.siteCode">
                    <td style="font-family: monospace;">{{ s.siteCode }}</td>
                    <td>{{ s.storeName }}</td>
                    <td class="text-right font-weight-bold">{{ num(s.qty) }}</td>
                  </tr>
                </tbody>
              </v-table>
              <v-row dense class="mt-3">
                <v-col cols="6">
                  <v-sheet class="pa-3 bg-grey-lighten-4 rounded">
                    <div class="text-caption text-medium-emphasis">{{ $t('labelOrder.label.summary.targetStores') }}</div>
                    <div class="text-h6">{{ selectedLines.length }} {{ $t('labelOrder.label.unitStore') }}</div>
                  </v-sheet>
                </v-col>
                <v-col cols="6">
                  <v-sheet class="pa-3 bg-blue-lighten-5 rounded">
                    <div class="text-caption text-medium-emphasis">{{ $t('labelOrder.label.summary.requestedTotal') }}</div>
                    <div class="text-h6 text-blue-darken-2">{{ num(selectedTotal) }} {{ $t('labelOrder.label.unitSheet') }}</div>
                  </v-sheet>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn variant="text" :disabled="isSubmitting" @click="confirmDialog = false">{{ $t('labelOrder.action.back') }}</v-btn>
              <v-btn class="px-6 bg-indigo-darken-1" elevation="2" :loading="isSubmitting" @click="submit">
                {{ $t('labelOrder.action.confirmSubmit') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import LabelMetaStrip from "@/components/label/LabelMetaStrip";
import api from "@/apis/staff";
import router from "@/router/index";
import Paths from "@/router/indexPaths";
import store from "@/store/index";
import utils from "@/utils";
import { num, remainState, remainColor, REMAIN_WARN, setFlash } from "@/utils/labelManagement";

export default {
  components: { AppWrapperView, LabelMetaStrip },
  data: () => ({
    stores: [],
    bulkQty: 5000,
    isLoading: false,
    message: "",
    errorMessage: "",
    confirmDialog: false,
    isSubmitting: false,
    submitError: "",
  }),
  computed: {
    // 選択されていて枚数が1以上の店舗
    selectedLines() {
      return this.stores.filter((s) => s.selected && Number(s.qty) > 0);
    },
    selectedTotal() {
      return this.selectedLines.reduce((a, s) => a + Number(s.qty), 0);
    },
    allSelected() {
      return this.stores.length > 0 && this.stores.every((s) => s.selected);
    },
    someSelected() {
      return this.stores.some((s) => s.selected);
    },
    infoItems() {
      return [
        { label: this.$t("labelOrder.label.requestId"), value: this.$t("labelOrder.label.autoNumber"), muted: true },
        { label: this.$t("labelOrder.label.requestDate"), value: utils.getCalendarStr(new Date()), strong: true },
        { label: this.$t("labelOrder.label.requestUser"), value: store.getUserName() },
        { label: this.$t("labelOrder.label.status"), value: this.$t("labelOrder.label.notRequested"), muted: true },
        { label: this.$t("labelOrder.label.deliveryDate"), value: this.$t("labelOrder.label.setOnApprove"), muted: true },
        { label: this.$t("labelOrder.label.reviewer"), value: "—", muted: true },
      ];
    },
    notEnteredCount() {
      return this.stores.filter((s) => !(Number(s.qty) > 0)).length;
    },
    lowStockCount() {
      return this.stores.filter((s) => s.remainingQty < REMAIN_WARN).length;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    num,
    remainState,
    remainColor,
    load() {
      this.isLoading = true;
      api.post("/label-stock/search", {})
        .then((response) => {
          if (response.data?.status == 1) {
            this.errorMessage = response.data.errorMessage;
            return;
          }
          this.stores = (response.data.results?.stockList ?? []).map((s) => ({ ...s, selected: false, qty: "" }));
        })
        .catch(() => {
          this.stores = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // 枚数を入れた店舗は自動で選択状態にする
    setQty(s, v) {
      s.qty = v;
      if (Number(v) > 0) s.selected = true;
    },
    toggleAll(on) {
      this.stores.forEach((s) => (s.selected = !!on));
    },
    bulkApply() {
      const targets = this.stores.filter((s) => s.selected);
      if (targets.length === 0) {
        this.message = this.$t("labelOrder.message.noSelection");
        return;
      }
      targets.forEach((s) => (s.qty = this.bulkQty));
      this.message = this.$t("labelOrder.message.bulkApplied", [targets.length, num(this.bulkQty)]);
    },
    bulkApplyAll() {
      this.stores.forEach((s) => {
        s.selected = true;
        s.qty = this.bulkQty;
      });
      this.message = this.$t("labelOrder.message.bulkApplied", [this.stores.length, num(this.bulkQty)]);
    },
    clearQty() {
      this.stores.forEach((s) => (s.qty = ""));
    },
    openConfirm() {
      if (this.selectedLines.length === 0) {
        this.message = this.$t("labelOrder.message.noInput");
        return;
      }
      this.submitError = "";
      this.confirmDialog = true;
    },
    submit() {
      this.isSubmitting = true;
      this.submitError = "";
      const lines = this.selectedLines.map((s) => ({ siteCode: s.siteCode, qty: Number(s.qty) }));
      api.post("/label-order/register", { lines })
        .then((response) => {
          if (response.data?.status == 1) {
            this.submitError = response.data.errorMessage;
            return;
          }
          setFlash(this.$t("labelOrder.message.registered", [response.data.results.requestId]));
          this.confirmDialog = false;
          router.push({ name: Paths.LABEL_ORDER });
        })
        .catch(() => {
          this.submitError = this.$t("common.message.error");
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    backToList() {
      router.push({ name: Paths.LABEL_ORDER });
    },
  },
};
</script>

<style scoped>
.text-right-input :deep(input) {
  text-align: right;
}
.qty-unselected :deep(input) {
  color: rgba(0, 0, 0, 0.38);
}
.label-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin-top: 16px;
  padding: 12px 4px;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
